/**
 * Report export helpers — CSV + PDF.
 *
 * Used by Reports.js (Rent Roll, Occupancy and the rest of the catalog).
 * Both helpers operate on the table-rows array that the report panel
 * already renders, so the on-screen and exported shape stay in lockstep.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const prettyHeader = (key) =>
  String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatCell = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
};

/**
 * Build a CSV string. Adds two commented header rows (facility, generated-
 * at) so the exported file documents its own provenance, then the column
 * header row, then data rows.
 */
export const buildCsv = (rows, { facilityName = '', reportName = '' } = {}) => {
  if (!rows || !rows.length) return '';
  const cols = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const now = new Date().toISOString();
  const preamble = [
    `# ${reportName || 'Report'}`,
    `# Facility: ${facilityName || '—'}`,
    `# Generated: ${now}`,
  ];
  return [
    ...preamble,
    cols.map(prettyHeader).join(','),
    ...rows.map((r) => cols.map((c) => esc(r[c])).join(',')),
  ].join('\n');
};

/**
 * Trigger a browser download for a string blob.
 */
export const downloadBlob = (filename, content, mime = 'text/csv') => {
  const blob = new Blob([content], { type: mime });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
};

/**
 * Render a PDF (landscape A4) with a heading block (facility name +
 * report name + generated-at) and an auto-fitted table of the rows.
 * Uses jspdf-autotable so column widths flex with the actual data.
 */
export const buildAndDownloadPdf = (
  rows,
  { facilityName = '', reportName = 'Report', filename = 'report.pdf' } = {},
) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const margin = 36;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(reportName, margin, margin + 4);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100);
  const subtitleParts = [];
  if (facilityName) subtitleParts.push(facilityName);
  subtitleParts.push(`Generated ${new Date().toLocaleString()}`);
  doc.text(subtitleParts.join('  ·  '), margin, margin + 20);
  doc.setTextColor(0);

  if (!rows || !rows.length) {
    doc.setFontSize(11);
    doc.text('No data for this report.', margin, margin + 50);
    doc.save(filename);
    return;
  }

  const cols = Object.keys(rows[0]);
  const head = [cols.map(prettyHeader)];
  const body = rows.map((r) => cols.map((c) => formatCell(r[c])));

  autoTable(doc, {
    head,
    body,
    startY: margin + 34,
    styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: margin, right: margin },
    didDrawPage: (data) => {
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(140);
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - margin,
        doc.internal.pageSize.getHeight() - 16,
        { align: 'right' },
      );
      doc.setTextColor(0);
    },
  });

  doc.save(filename);
};
